import React, { useCallback, useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { Archive, ExternalLink, LayoutTemplate, Lightbulb, Loader2, Pencil, Plus, Search, Send, Trash2 } from 'lucide-react';
import { Button } from '@components/Button';
import { Dialog, ConfirmDialog } from '@components/Dialog';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { ColorPicker } from '@components/ColorPicker';
import { DateInputBR } from '@components/DateInputBR';
import { usePermissions } from '@hooks/usePermissions';
import { useSocialPost } from '@contexts/SocialPostContext';
import socialService from '@services/socialService';
import type { Idea, IdeaKind, Network, Pillar, PillarBalance, SaveIdeaRequest, SavePillarRequest } from '@/types/social';
import { IDEA_KIND_LABELS, NETWORKS, NETWORK_LABELS } from '@/types/social';
import { colorClasses } from '@/utils/colors';
import { addDaysISO, formatDateBR, todayISO } from '@/utils/formatters';
import { NetworkIcon } from '@components/social/SocialBits';
import { PillarBalancePanel } from './SocialGridView';

const emptyIdea = (): SaveIdeaRequest => ({ kind: 'idea', title: '', body: '', network: null, pillar_id: null, link_url: '', valid_until: null });
const emptyPillar = (): SavePillarRequest => ({ name: '', color: 'sky', description: '', target_percent: null, is_active: true });

/**
 * Ideias e estratégia: banco de pautas, ganchos, roteiros e áudios em alta (curadoria manual,
 * com validade) e os pilares de conteúdo (blocos de estratégia) com meta de proporção.
 * Documentação: src/docs/redes-sociais/ideias.md.
 */
export const SocialIdeasView: React.FC = () => {
  const [tab, setTab] = useState<'ideas' | 'pillars'>('ideas');
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [pillars, setPillars] = useState<Pillar[]>([]);
  const [balance, setBalance] = useState<PillarBalance[]>([]);
  const [kind, setKind] = useState<IdeaKind | ''>('');
  const [network, setNetwork] = useState<Network | ''>('');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [archived, setArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [ideaForm, setIdeaForm] = useState<{ id: number | null; data: SaveIdeaRequest } | null>(null);
  const [pillarForm, setPillarForm] = useState<{ id: number | null; data: SavePillarRequest } | null>(null);
  const [toDelete, setToDelete] = useState<Idea | null>(null);
  const [saving, setSaving] = useState(false);
  const { openPost, openNewPost } = useSocialPost();
  const { checkPermission } = usePermissions();
  const canWrite = checkPermission('social.write').hasPermission;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const today = todayISO();
      const [i, p, b] = await Promise.all([
        socialService.listIdeas({ kind, network, search: query, includeArchived: archived }),
        socialService.listPillars(true),
        socialService.balance(addDaysISO(today, -30), addDaysISO(today, 30)),
      ]);
      setIdeas(i);
      setPillars(p);
      setBalance(b);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [kind, network, query, archived]);

  useEffect(() => {
    load();
  }, [load]);

  const saveIdea = async () => {
    if (!ideaForm) return;
    setSaving(true);
    try {
      await socialService.saveIdea(ideaForm.id, ideaForm.data);
      setIdeaForm(null);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  const savePillar = async () => {
    if (!pillarForm) return;
    setSaving(true);
    try {
      await socialService.savePillar(pillarForm.id, pillarForm.data);
      setPillarForm(null);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  const toPost = async (i: Idea) => {
    try {
      const p = await socialService.ideaToPost(i.id);
      await load();
      openPost(p.id);
    } catch (err) {
      setError(err);
    }
  };

  const archive = async (i: Idea) => {
    try {
      await socialService.saveIdea(i.id, { ...i, body: i.body ?? null, is_archived: !i.is_archived });
      await load();
    } catch (err) {
      setError(err);
    }
  };

  const setIdea = (patch: Partial<SaveIdeaRequest>) => setIdeaForm((f) => (f ? { ...f, data: { ...f.data, ...patch } } : f));
  const setPillar = (patch: Partial<SavePillarRequest>) => setPillarForm((f) => (f ? { ...f, data: { ...f.data, ...patch } } : f));
  const pillarName = (id?: number) => pillars.find((p) => p.id === id);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap rounded-lg border border-slate-200 bg-white overflow-hidden">
          <button type="button" onClick={() => setTab('ideas')} className={clsx('px-4 py-1.5 text-sm inline-flex items-center gap-1', tab === 'ideas' ? 'bg-emerald-600 text-white' : 'text-slate-600')}>
            <Lightbulb size={14} /> Banco de ideias
          </button>
          <button type="button" onClick={() => setTab('pillars')} className={clsx('px-4 py-1.5 text-sm inline-flex items-center gap-1', tab === 'pillars' ? 'bg-emerald-600 text-white' : 'text-slate-600')}>
            <LayoutTemplate size={14} /> Pilares e blocos de estratégia
          </button>
        </div>
        {loading && <Loader2 size={16} className="animate-spin text-slate-400" />}
        {canWrite && tab === 'ideas' && (
          <Button size="sm" icon={<Plus />} onClick={() => setIdeaForm({ id: null, data: emptyIdea() })}>
            Nova ideia
          </Button>
        )}
        {canWrite && tab === 'pillars' && (
          <Button size="sm" icon={<Plus />} onClick={() => setPillarForm({ id: null, data: emptyPillar() })}>
            Novo pilar
          </Button>
        )}
      </div>

      {tab === 'ideas' && (
        <>
          <form
            className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setQuery(search.trim());
            }}
          >
            <div className="relative col-span-2">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input className="input-field w-full sm:w-56 pl-8" placeholder="Buscar" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <select className="select-field w-full sm:w-40" value={kind} onChange={(e) => setKind(e.target.value as IdeaKind | '')}>
              <option value="">Todos os tipos</option>
              {Object.entries(IDEA_KIND_LABELS).map(([k, l]) => (
                <option key={k} value={k}>
                  {l}
                </option>
              ))}
            </select>
            <select className="select-field w-full sm:w-36" value={network} onChange={(e) => setNetwork(e.target.value as Network | '')}>
              <option value="">Todas as redes</option>
              {NETWORKS.map((n) => (
                <option key={n} value={n}>
                  {NETWORK_LABELS[n]}
                </option>
              ))}
            </select>
            <label className="inline-flex items-center gap-1.5 text-sm text-slate-600">
              <input type="checkbox" className="checkbox-field" checked={archived} onChange={(e) => setArchived(e.target.checked)} /> Mostrar arquivadas
            </label>
            <Button type="submit" size="sm" variant="outline" icon={<Search />}>
              Buscar
            </Button>
          </form>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {ideas.map((i) => {
              const pl = pillarName(i.pillar_id);
              return (
                <div key={i.id} className={clsx('card p-3 space-y-2', i.is_archived && 'opacity-60')}>
                  <div className="flex items-center gap-2">
                    <span className="badge-neutral">{IDEA_KIND_LABELS[i.kind]}</span>
                    {i.network && <NetworkIcon network={i.network} />}
                    {pl && <span className={clsx('inline-block w-2 h-2 rounded-full', colorClasses(pl.color).swatch)} title={pl.name} />}
                    {i.is_expired && <span className="badge-danger">vencida</span>}
                    {i.used_in_post_id && (
                      <button type="button" className="badge-success" onClick={() => openPost(i.used_in_post_id!)}>
                        virou post
                      </button>
                    )}
                  </div>
                  <p className="font-semibold text-slate-800">{i.title}</p>
                  {i.body && <p className="text-sm text-slate-600 whitespace-pre-wrap line-clamp-5">{i.body}</p>}
                  <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                    {i.valid_until && <span>válida até {formatDateBR(i.valid_until)}</span>}
                    {i.link_url && (
                      <a href={i.link_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-emerald-700 hover:underline">
                        <ExternalLink size={11} /> referência
                      </a>
                    )}
                  </div>
                  {canWrite && (
                    <div className="flex flex-wrap gap-1 pt-1">
                      <Button size="sm" variant="outline" icon={<Send />} onClick={() => toPost(i)}>
                        Virar post
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        icon={<Pencil />}
                        onClick={() =>
                          setIdeaForm({
                            id: i.id,
                            data: { kind: i.kind, title: i.title, body: i.body ?? '', network: i.network ?? null, pillar_id: i.pillar_id ?? null, link_url: i.link_url ?? '', valid_until: i.valid_until ?? null, is_archived: i.is_archived },
                          })
                        }
                      >
                        Editar
                      </Button>
                      <Button size="sm" variant="outline" icon={<Archive />} onClick={() => archive(i)}>
                        {i.is_archived ? 'Desarquivar' : 'Arquivar'}
                      </Button>
                      <Button size="sm" variant="outline" icon={<Trash2 />} onClick={() => setToDelete(i)}>
                        {''}
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
            {ideas.length === 0 && !loading && <p className="text-sm text-slate-500">Nenhuma ideia ainda.</p>}
          </div>
        </>
      )}

      {tab === 'pillars' && (
        <div className="flex flex-col xl:flex-row gap-4">
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
            {pillars.map((p) => {
              const cc = colorClasses(p.color);
              return (
                <div key={p.id} className={clsx('card p-3 border-l-4', cc.borderLeft, !p.is_active && 'opacity-50')}>
                  <div className="flex items-center justify-between">
                    <p className={clsx('font-semibold', cc.textStrong)}>{p.name}</p>
                    <span className="text-xs text-slate-500">{p.target_percent !== undefined ? `meta ${p.target_percent}%` : 'sem meta'}</span>
                  </div>
                  {p.description && <p className="text-sm text-slate-600 mt-1">{p.description}</p>}
                  {!p.is_active && <p className="text-xs text-slate-400">inativo</p>}
                  {canWrite && (
                    <div className="flex gap-1 mt-2">
                      <Button size="sm" variant="outline" icon={<LayoutTemplate />} onClick={() => openNewPost({ strategy: true, pillar_id: p.id, title: p.name })}>
                        Novo bloco
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        icon={<Pencil />}
                        onClick={() =>
                          setPillarForm({
                            id: p.id,
                            data: { name: p.name, color: p.color, description: p.description ?? '', target_percent: p.target_percent ?? null, sort_order: p.sort_order, is_active: p.is_active },
                          })
                        }
                      >
                        Editar
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <aside className="xl:w-80 flex-shrink-0">
            <PillarBalancePanel balance={balance} title="Equilíbrio (30 dias para trás e para frente)" />
          </aside>
        </div>
      )}

      <Dialog
        isOpen={!!ideaForm}
        onClose={() => setIdeaForm(null)}
        title={ideaForm?.id ? 'Editar ideia' : 'Nova ideia'}
        size="lg"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIdeaForm(null)}>
              Cancelar
            </Button>
            <Button loading={saving} onClick={saveIdea}>
              Salvar
            </Button>
          </div>
        }
      >
        {ideaForm && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <select className="select-field" value={ideaForm.data.kind} onChange={(e) => setIdea({ kind: e.target.value as IdeaKind })}>
                {Object.entries(IDEA_KIND_LABELS).map(([k, l]) => (
                  <option key={k} value={k}>
                    {l}
                  </option>
                ))}
              </select>
              <select className="select-field" value={ideaForm.data.network ?? ''} onChange={(e) => setIdea({ network: (e.target.value || null) as Network | null })}>
                <option value="">Qualquer rede</option>
                {NETWORKS.map((n) => (
                  <option key={n} value={n}>
                    {NETWORK_LABELS[n]}
                  </option>
                ))}
              </select>
            </div>
            <input className="input-field w-full" placeholder="Título" value={ideaForm.data.title} onChange={(e) => setIdea({ title: e.target.value })} />
            <textarea
              className="input-field w-full min-h-[120px]"
              placeholder={ideaForm.data.kind === 'hook' ? 'O gancho (primeiros 3 segundos do vídeo)' : ideaForm.data.kind === 'script' ? 'Roteiro cena a cena' : 'Detalhes'}
              value={ideaForm.data.body ?? ''}
              onChange={(e) => setIdea({ body: e.target.value })}
            />
            <div className="grid grid-cols-2 gap-3">
              <select className="select-field" value={ideaForm.data.pillar_id ?? ''} onChange={(e) => setIdea({ pillar_id: e.target.value ? Number(e.target.value) : null })}>
                <option value="">Sem pilar</option>
                {pillars.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              <DateInputBR value={ideaForm.data.valid_until ?? ''} onChange={(v: string) => setIdea({ valid_until: v || null })} placeholder="Válida até" />
            </div>
            <input
              className="input-field w-full"
              placeholder={ideaForm.data.kind === 'audio' ? 'Link do áudio (TikTok Creative Center, Instagram)' : 'Link de referência'}
              value={ideaForm.data.link_url ?? ''}
              onChange={(e) => setIdea({ link_url: e.target.value })}
            />
          </div>
        )}
      </Dialog>

      <Dialog
        isOpen={!!pillarForm}
        onClose={() => setPillarForm(null)}
        title={pillarForm?.id ? 'Editar pilar' : 'Novo pilar'}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setPillarForm(null)}>
              Cancelar
            </Button>
            <Button loading={saving} onClick={savePillar}>
              Salvar
            </Button>
          </div>
        }
      >
        {pillarForm && (
          <div className="space-y-3">
            <input className="input-field w-full" placeholder="Nome (Dica, Venda, Bastidores...)" value={pillarForm.data.name} onChange={(e) => setPillar({ name: e.target.value })} />
            <ColorPicker value={pillarForm.data.color} onChange={(c: string) => setPillar({ color: c })} />
            <textarea className="input-field w-full" placeholder="Que tipo de conteúdo entra aqui" value={pillarForm.data.description ?? ''} onChange={(e) => setPillar({ description: e.target.value })} />
            <div className="flex items-center gap-3">
              <label className="text-sm text-slate-600">Meta no feed (%)</label>
              <input
                className="input-field w-24"
                inputMode="numeric"
                value={pillarForm.data.target_percent ?? ''}
                onChange={(e) => setPillar({ target_percent: e.target.value === '' ? null : Number.parseInt(e.target.value, 10) || 0 })}
              />
              <label className="inline-flex items-center gap-1.5 text-sm text-slate-600">
                <input type="checkbox" className="checkbox-field" checked={pillarForm.data.is_active ?? true} onChange={(e) => setPillar({ is_active: e.target.checked })} /> Ativo
              </label>
            </div>
          </div>
        )}
      </Dialog>

      <ConfirmDialog
        isOpen={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={async () => {
          if (!toDelete) return;
          try {
            await socialService.deleteIdea(toDelete.id);
            setToDelete(null);
            await load();
          } catch (err) {
            setError(err);
          }
        }}
        title="Excluir ideia"
        message={`Excluir "${toDelete?.title ?? ''}"? Para só tirar da lista, use Arquivar.`}
        confirmText="Excluir"
        severity="danger"
      />
      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </div>
  );
};

export default SocialIdeasView;
